import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import * as XLSX from 'xlsx'

const fmt = v => v == null ? '—' : Number(v).toLocaleString('en-US',{minimumFractionDigits:0,maximumFractionDigits:0})
function monthKey(d) { return d.toISOString().slice(0,7) }
function monthLabel(ym) {
  const [y,m] = ym.split('-')
  return ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][parseInt(m)-1]+" '"+y.slice(2)
}
function firstOfMonth(ym) { return ym+'-01' }

export default function FinancialFees({ projectId }) {
  const [settings, setSettings]     = useState(null)
  const [showSettings, setShowSettings] = useState(false)
  const [settingsForm, setSettingsForm] = useState({ project_start_month:'', project_end_month:'' })
  const [months, setMonths]         = useState([])
  const [entries, setEntries]       = useState([])  // rows from financial_fees_entries
  const [rows, setRows]             = useState([])  // description rows user defines
  const [dist, setDist]             = useState({})  // { [desc]: { [ym]: amount } }
  const [dirty, setDirty]           = useState({})
  const [saving, setSaving]         = useState(false)
  const [showAddRow, setShowAddRow] = useState(false)
  const [newRowDesc, setNewRowDesc] = useState('')
  const [newRowSign, setNewRowSign] = useState('-')  // '+' or '-'
  const [loading, setLoading]       = useState(true)

  useEffect(() => { if (projectId) loadAll(projectId) }, [projectId])

  async function loadAll(pid) {
    setLoading(true)
    await loadSettings(pid)
    await loadEntries(pid)
    setLoading(false)
  }

  async function loadSettings(pid) {
    const { data } = await supabase.from('cashflow_settings').select('*').eq('project_id', pid).maybeSingle()
    setSettings(data)
    if (data?.project_start_month && data?.project_end_month) {
      setMonths(buildMonths(data.project_start_month, data.project_end_month))
      setSettingsForm({ project_start_month: data.project_start_month.slice(0,7), project_end_month: data.project_end_month.slice(0,7) })
    }
  }

  function buildMonths(start, end) {
    const ms=[]; const s=new Date(start); s.setDate(1); const e=new Date(end); e.setDate(1)
    while(s<=e){ms.push(monthKey(s));s.setMonth(s.getMonth()+1)}
    return ms
  }

  async function saveSettings() {
    const payload={ project_id:projectId, project_start_month:firstOfMonth(settingsForm.project_start_month), project_end_month:firstOfMonth(settingsForm.project_end_month) }
    const { data:ex } = await supabase.from('cashflow_settings').select('id').eq('project_id',projectId).maybeSingle()
    if(ex) await supabase.from('cashflow_settings').update(payload).eq('project_id',projectId)
    else await supabase.from('cashflow_settings').insert(payload)
    setShowSettings(false); loadSettings(projectId)
  }

  async function loadEntries(pid) {
    const { data } = await supabase.from('financial_fees_entries').select('*').eq('project_id',pid).order('description')
    const descs = [...new Set((data||[]).map(r=>r.description).filter(Boolean))]
    setRows(descs.map(d => ({ desc: d, sign: (data||[]).find(r=>r.description===d)?.amount < 0 ? '-' : '+' })))
    const map = {}
    for(const r of (data||[])) {
      if(!map[r.description]) map[r.description]={}
      map[r.description][r.period_month.slice(0,7)] = Math.abs(parseFloat(r.amount)||0)
    }
    setDist(map)
    setEntries(data||[])
  }

  function handleCell(desc, ym, val) {
    const num = parseFloat(val)||0
    setDist(d=>({...d,[desc]:{...(d[desc]||{}),[ym]:num}}))
    setDirty(d=>({...d,[`${desc}|${ym}`]:true}))
  }

  async function saveAll() {
    setSaving(true)
    for(const row of rows) {
      for(const ym of months) {
        const key=`${row.desc}|${ym}`
        if(!dirty[key]) continue
        const rawAmt = dist[row.desc]?.[ym]||0
        const amt = row.sign==='-' ? -rawAmt : rawAmt
        const period = firstOfMonth(ym)
        const existing = entries.find(e=>e.description===row.desc && e.period_month.slice(0,7)===ym)
        if(existing) await supabase.from('financial_fees_entries').update({amount:amt}).eq('id',existing.id)
        else await supabase.from('financial_fees_entries').insert({project_id:projectId,period_month:period,description:row.desc,amount:amt})
      }
    }
    setDirty({}); setSaving(false); loadEntries(projectId)
  }

  async function addRow() {
    if(!newRowDesc.trim()) return
    setRows(r=>[...r,{desc:newRowDesc.trim(),sign:newRowSign}])
    setShowAddRow(false); setNewRowDesc(''); setNewRowSign('-')
  }

  async function deleteRow(desc) {
    if(!window.confirm(`حذف "${desc}"؟`)) return
    await supabase.from('financial_fees_entries').delete().eq('project_id',projectId).eq('description',desc)
    loadEntries(projectId)
  }

  function handleUpload(e) {
    const file=e.target.files[0]; if(!file) return
    const reader=new FileReader()
    reader.onload=ev=>{
      const wb=XLSX.read(ev.target.result,{type:'binary'})
      const ws=wb.Sheets[wb.SheetNames[0]]
      const aoa=XLSX.utils.sheet_to_json(ws,{header:1,defval:null})
      const hdrs=aoa[0]||[]
      const newDist={...dist}; const newDirty={...dirty}; const newRows=[...rows]
      for(let i=1;i<aoa.length;i++){
        const row=aoa[i]; if(!row[0]) continue
        const desc=String(row[0]).trim()
        if(!newRows.find(r=>r.desc===desc)) newRows.push({desc,sign:'-'})
        if(!newDist[desc]) newDist[desc]={}
        for(let j=1;j<hdrs.length;j++){
          const ym=hdrs[j]?String(hdrs[j]).slice(0,7):null; if(!ym) continue
          newDist[desc][ym]=Math.abs(parseFloat(row[j])||0)
          newDirty[`${desc}|${ym}`]=true
        }
      }
      setRows(newRows); setDist(newDist); setDirty(newDirty)
    }
    reader.readAsBinaryString(file)
  }

  function downloadTemplate() {
    const hdr=['Description',...months]
    const dataRows=rows.map(r=>[r.desc,...months.map(ym=>dist[r.desc]?.[ym]||0)])
    const ws=XLSX.utils.aoa_to_sheet([hdr,...dataRows])
    const wb=XLSX.utils.book_new(); XLSX.utils.book_append_sheet(wb,ws,'Fees')
    XLSX.writeFile(wb,'financial_fees_template.xlsx')
  }

  const thS={padding:'8px 10px',fontSize:11,fontWeight:700,background:'#1B3A6B',color:'#fff',whiteSpace:'nowrap',textAlign:'right',position:'sticky',top:0}
  const thSL={...thS,textAlign:'left',minWidth:200}
  const tdS={padding:'6px 8px',fontSize:11,borderBottom:'1px solid #E5E7EB',textAlign:'right',fontFamily:'monospace'}
  const tdSL={...tdS,textAlign:'left',fontWeight:600}

  if(loading) return <div style={{padding:40,textAlign:'center',color:'#6B7280'}}>Loading...</div>

  if(!settings?.project_start_month) return (
    <div style={{padding:40,textAlign:'center'}}>
      <p style={{color:'#6B7280',marginBottom:16}}>حدد أولاً فترة المشروع</p>
      <button onClick={()=>setShowSettings(true)}
        style={{padding:'10px 24px',background:'#1B3A6B',color:'#fff',borderRadius:8,border:'none',cursor:'pointer',fontWeight:700}}>
        ⚙ تحديد فترة المشروع
      </button>
      {showSettings && renderSettingsModal()}
    </div>
  )

  // Monthly totals
  const monthTotals = months.reduce((acc,ym)=>{
    const t=rows.reduce((s,r)=>{
      const raw=dist[r.desc]?.[ym]||0
      return s+(r.sign==='-'?-raw:raw)
    },0)
    acc[ym]=t; return acc
  },{})

  function renderSettingsModal(){
    return(
      <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:50}}>
        <div style={{background:'#fff',borderRadius:16,padding:32,width:380,boxShadow:'0 20px 60px rgba(0,0,0,.2)'}}>
          <h3 style={{fontSize:16,fontWeight:700,marginBottom:20}}>⚙ فترة المشروع</h3>
          <div style={{marginBottom:16}}>
            <label style={{fontSize:12,color:'#6B7280',display:'block',marginBottom:6}}>شهر البداية</label>
            <input type="month" value={settingsForm.project_start_month} onChange={e=>setSettingsForm(p=>({...p,project_start_month:e.target.value}))}
              style={{width:'100%',border:'1px solid #E5E7EB',borderRadius:8,padding:'8px 12px',fontSize:13}}/>
          </div>
          <div style={{marginBottom:24}}>
            <label style={{fontSize:12,color:'#6B7280',display:'block',marginBottom:6}}>شهر النهاية</label>
            <input type="month" value={settingsForm.project_end_month} onChange={e=>setSettingsForm(p=>({...p,project_end_month:e.target.value}))}
              style={{width:'100%',border:'1px solid #E5E7EB',borderRadius:8,padding:'8px 12px',fontSize:13}}/>
          </div>
          <div style={{display:'flex',gap:10}}>
            <button onClick={()=>setShowSettings(false)} style={{flex:1,padding:'10px',border:'1px solid #E5E7EB',borderRadius:8,cursor:'pointer',background:'#fff'}}>إلغاء</button>
            <button onClick={saveSettings} style={{flex:1,padding:'10px',background:'#1B3A6B',color:'#fff',borderRadius:8,border:'none',cursor:'pointer',fontWeight:700}}>حفظ</button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div>
      <div style={{display:'flex',gap:8,marginBottom:16,flexWrap:'wrap',alignItems:'center'}}>
        <button onClick={()=>setShowSettings(true)}
          style={{padding:'6px 14px',border:'1px solid #E5E7EB',borderRadius:6,background:'#fff',fontSize:12,cursor:'pointer'}}>
          ⚙ {settings?.project_start_month?.slice(0,7)} → {settings?.project_end_month?.slice(0,7)}
        </button>
        <button onClick={()=>setShowAddRow(true)}
          style={{padding:'6px 14px',background:'#1B3A6B',color:'#fff',borderRadius:6,border:'none',fontSize:12,cursor:'pointer',fontWeight:600}}>
          + Add Row
        </button>
        <button onClick={downloadTemplate}
          style={{padding:'6px 14px',border:'1px solid #E5E7EB',borderRadius:6,background:'#fff',fontSize:12,cursor:'pointer'}}>
          ↓ Template
        </button>
        <label style={{padding:'6px 14px',border:'1px solid #E5E7EB',borderRadius:6,background:'#fff',fontSize:12,cursor:'pointer'}}>
          ↑ Upload
          <input type="file" accept=".xlsx,.xls" onChange={handleUpload} style={{display:'none'}}/>
        </label>
        {Object.keys(dirty).length>0 && (
          <button onClick={saveAll} disabled={saving}
            style={{padding:'6px 18px',background:'#16a34a',color:'#fff',borderRadius:6,border:'none',fontSize:12,fontWeight:700,cursor:'pointer'}}>
            {saving?'⏳ Saving...':`💾 Save (${Object.keys(dirty).length})`}
          </button>
        )}
      </div>

      <div style={{overflowX:'auto',borderRadius:10,border:'1px solid #E5E7EB',boxShadow:'0 1px 4px rgba(0,0,0,.06)'}}>
        <table style={{borderCollapse:'collapse',width:'100%'}}>
          <thead>
            <tr>
              <th style={{...thSL,minWidth:220}}>Description</th>
              <th style={{...thS,minWidth:60}}>+/−</th>
              {months.map(ym=><th key={ym} style={{...thS,minWidth:90}}>{monthLabel(ym)}</th>)}
              <th style={{...thS,background:'#0F2A50',minWidth:80}}>Total</th>
              <th style={{...thS,minWidth:50}}></th>
            </tr>
          </thead>
          <tbody>
            {rows.length===0 && (
              <tr><td colSpan={3+months.length} style={{padding:24,textAlign:'center',color:'#9CA3AF',fontSize:12}}>لا توجد بنود — اضغط + Add Row</td></tr>
            )}
            {rows.map(row=>{
              const rowTotal=months.reduce((s,ym)=>{
                const raw=dist[row.desc]?.[ym]||0
                return s+(row.sign==='-'?-raw:raw)
              },0)
              return(
                <tr key={row.desc} style={{background:'#fff'}}
                  onMouseEnter={e=>e.currentTarget.style.background='#F9FAFB'}
                  onMouseLeave={e=>e.currentTarget.style.background='#fff'}>
                  <td style={tdSL}>{row.desc}</td>
                  <td style={{...tdS,textAlign:'center'}}>
                    <span style={{padding:'2px 8px',borderRadius:12,fontSize:10,fontWeight:700,background:row.sign==='-'?'rgba(220,38,38,.1)':'rgba(22,163,74,.1)',color:row.sign==='-'?'#dc2626':'#16a34a'}}>
                      {row.sign==='-'?'OUT':'IN'}
                    </span>
                  </td>
                  {months.map(ym=>(
                    <td key={ym} style={{padding:2,borderBottom:'1px solid #E5E7EB'}}>
                      <input type="number" value={dist[row.desc]?.[ym]||''}
                        onChange={e=>handleCell(row.desc,ym,e.target.value)}
                        style={{width:88,padding:'4px 6px',fontSize:11,border:dirty[`${row.desc}|${ym}`]?'1px solid #1B3A6B':'1px solid #E5E7EB',borderRadius:4,textAlign:'right',fontFamily:'monospace',background:dirty[`${row.desc}|${ym}`]?'#EFF6FF':'#fff'}}/>
                    </td>
                  ))}
                  <td style={{...tdS,fontWeight:700,color:rowTotal<0?'#dc2626':rowTotal>0?'#16a34a':'#6B7280'}}>{fmt(rowTotal)}</td>
                  <td style={{...tdS,textAlign:'center'}}>
                    <button onClick={()=>deleteRow(row.desc)} style={{background:'none',border:'none',cursor:'pointer',color:'#dc2626',fontSize:14}}>🗑</button>
                  </td>
                </tr>
              )
            })}
            {/* Total row */}
            {rows.length>0 && (
              <tr style={{background:'#1B3A6B',fontWeight:700}}>
                <td style={{...tdSL,color:'#fff',position:'sticky',left:0,background:'#1B3A6B'}}>TOTAL</td>
                <td style={{...tdS,color:'#fff'}}/>
                {months.map(ym=><td key={ym} style={{...tdS,color:'#fff',fontWeight:700}}>{monthTotals[ym]?fmt(monthTotals[ym]):'—'}</td>)}
                <td style={{...tdS,color:'#fff',fontWeight:700}}>{fmt(Object.values(monthTotals).reduce((s,v)=>s+v,0))}</td>
                <td/>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Add Row Modal */}
      {showAddRow && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:50}}>
          <div style={{background:'#fff',borderRadius:16,padding:28,width:360,boxShadow:'0 20px 60px rgba(0,0,0,.2)'}}>
            <h3 style={{fontSize:15,fontWeight:700,marginBottom:16}}>+ Add Fee Row</h3>
            <div style={{marginBottom:14}}>
              <label style={{fontSize:12,color:'#6B7280',display:'block',marginBottom:6}}>Description</label>
              <input value={newRowDesc} onChange={e=>setNewRowDesc(e.target.value)}
                placeholder="e.g. Bank Charges, Insurance..."
                style={{width:'100%',border:'1px solid #E5E7EB',borderRadius:8,padding:'8px 12px',fontSize:13,boxSizing:'border-box'}}/>
            </div>
            <div style={{marginBottom:20}}>
              <label style={{fontSize:12,color:'#6B7280',display:'block',marginBottom:6}}>Type</label>
              <div style={{display:'flex',gap:10}}>
                {[{v:'-',l:'Cash Out (−)'},{v:'+',l:'Cash In (+)'}].map(opt=>(
                  <button key={opt.v} onClick={()=>setNewRowSign(opt.v)}
                    style={{flex:1,padding:'8px',borderRadius:8,border:`2px solid ${newRowSign===opt.v?(opt.v==='-'?'#dc2626':'#16a34a'):'#E5E7EB'}`,background:newRowSign===opt.v?(opt.v==='-'?'rgba(220,38,38,.08)':'rgba(22,163,74,.08)'):'#fff',cursor:'pointer',fontSize:12,fontWeight:600,color:opt.v==='-'?'#dc2626':'#16a34a'}}>
                    {opt.l}
                  </button>
                ))}
              </div>
            </div>
            <div style={{display:'flex',gap:10}}>
              <button onClick={()=>setShowAddRow(false)} style={{flex:1,padding:'10px',border:'1px solid #E5E7EB',borderRadius:8,cursor:'pointer'}}>إلغاء</button>
              <button onClick={addRow} style={{flex:1,padding:'10px',background:'#1B3A6B',color:'#fff',borderRadius:8,border:'none',cursor:'pointer',fontWeight:700}}>Add</button>
            </div>
          </div>
        </div>
      )}

      {showSettings && renderSettingsModal()}
    </div>
  )
}
